interface LocalStorageConfig<T> {
    key: string;
    defaultValue: T;
    validator?: (value: any) => value is T;
}

/**
 * Centralized localStorage management utility
 */
export class LocalStorageManager {
    /**
     * Get value from localStorage with validation and error handling
     */
    static get<T>(config: LocalStorageConfig<T>): T {
        try {
            const savedValue = localStorage.getItem(config.key);
            if (savedValue) {
                const parsedValue = JSON.parse(savedValue);

                // Validate the parsed value if validator is provided
                if (config.validator && !config.validator(parsedValue)) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Invalid state structure for key "${config.key}", using default`,
                    );
                    return config.defaultValue;
                }

                return parsedValue;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(
                `Failed to parse localStorage value for key "${config.key}":`,
                error,
            );
        }

        return config.defaultValue;
    }

    /**
     * Set value in localStorage with error handling
     */
    static set<T>(key: string, value: T): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `Failed to save value to localStorage for key "${key}":`,
                error,
            );
        }
    }

    /**
     * Remove value from localStorage
     */
    static remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Failed to remove localStorage key "${key}":`, error);
        }
    }

    /**
     * Clear all localStorage (use with caution)
     */
    static clear(): void {
        try {
            localStorage.clear();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to clear localStorage:', error);
        }
    }

    /**
     * Check if localStorage is available
     */
    static isAvailable(): boolean {
        try {
            const testKey = '__localStorage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }
}
